/**
 * MapDisplay ユーティリティ関数
 */

import { PLAYER_COLORS, UNOWNED_TRACK_COLOR } from '../../types';

/**
 * 所有者IDから線路色を取得
 */
export function getTrackColorByOwner(owner: string | undefined): string {
  if (!owner || owner === '') {
    return UNOWNED_TRACK_COLOR;
  }
  return PLAYER_COLORS[owner] ?? UNOWNED_TRACK_COLOR;
}

/**
 * TrackTile から各線路の所有者色配列を取得
 * 複線タイルの場合、owners フィールドを参照して各線路の色を返す
 */
export function getTrackColorsByOwners(tile: { owner: string; owners?: Array<{ playerId: string; connectionIndex: number }> }): string[] {
  // owners が設定されている場合は各線路の所有者色を返す
  if (tile.owners && tile.owners.length > 0) {
    // connectionIndex の最大値 + 1 が配列サイズ
    const maxIndex = Math.max(...tile.owners.map(o => o.connectionIndex));
    const colors: string[] = new Array(maxIndex + 1).fill(UNOWNED_TRACK_COLOR);

    for (const ownership of tile.owners) {
      colors[ownership.connectionIndex] = getTrackColorByOwner(ownership.playerId);
    }
    return colors;
  }

  // owners がない場合は owner から単一色を配列で返す
  return [getTrackColorByOwner(tile.owner)];
}

/**
 * Axial座標をピクセル座標に変換
 */
export function axialToPixel(q: number, r: number, hexSize: number): { x: number; y: number } {
  const x = hexSize * (Math.sqrt(3) * q + (Math.sqrt(3) / 2) * r);
  const y = hexSize * ((3 / 2) * r);
  return { x, y };
}

/**
 * ヘクスの頂点座標を計算（Pointy-top orientation）
 *
 * Pointy-top hex は上部が角（頂点）:
 *        5  0
 *         \/
 *      4-[HEX]-1
 *         /\
 *        3  2
 *
 * 頂点はコンパス方位角0°（北=上）から始まり、時計回りに60°ずつ配置
 * コンパス角θ → 画面座標: x = sin(θ), y = -cos(θ)
 */
export function getHexPoints(centerX: number, centerY: number, hexSize: number): string {
  const points: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    // 頂点のコンパス方位角: 0°, 60°, 120°, 180°, 240°, 300°
    const compassAngle = (i * 60) * Math.PI / 180;
    const x = centerX + hexSize * Math.sin(compassAngle);
    const y = centerY - hexSize * Math.cos(compassAngle);
    points.push([x, y]);
  }
  return points.map((p) => p.join(',')).join(' ');
}

/**
 * 辺の中点座標を計算（Pointy-top orientation）
 *
 * 辺番号と角度（コンパス方位角: 北=0°、時計回りが正）:
 * - 0 = NE（北東） = 30°
 * - 1 = E（東）   = 90°
 * - 2 = SE（南東） = 150°
 * - 3 = SW（南西） = 210°
 * - 4 = W（西）   = 270°
 * - 5 = NW（北西） = 330°
 *
 * コンパス角θ → 画面座標: x = sin(θ), y = -cos(θ)
 */
export function getEdgeMidpoint(cx: number, cy: number, size: number, edge: number): { x: number; y: number } {
  const compassAngles = [30, 90, 150, 210, 270, 330];
  const θ = compassAngles[edge] * Math.PI / 180;
  // 中心から辺の中点までの距離 = size * cos(30°)
  const distance = size * Math.cos(Math.PI / 6);
  return {
    x: cx + distance * Math.sin(θ),
    y: cy - distance * Math.cos(θ),
  };
}

/**
 * タイルタイプから接続情報を取得
 */
export function getTileConnections(tileType: string): number[][] {
  const connectionMap: Record<string, number[][]> = {
    // Simple Tiles
    'STRAIGHT': [[0, 3]],
    'ACUTE_CURVE': [[0, 1]],
    'GENTLE_CURVE': [[0, 2]],
    // Crossing Tiles
    'GENTLE_STRAIGHT_CROSS': [[0, 3], [1, 5]],
    'TWO_GENTLE_CROSS': [[0, 2], [1, 3]],
    'TWO_STRAIGHT_CROSS': [[0, 3], [1, 4]],
    // Coexist Tiles
    'TWO_GENTLE_COEXIST': [[0, 2], [3, 5]],
    'LEFT_GENTLE_ACUTE': [[0, 2], [3, 4]],
    'RIGHT_GENTLE_ACUTE': [[0, 1], [2, 4]],
    'STRAIGHT_ACUTE': [[0, 3], [4, 5]],
    // Town Tiles
    'TOWN_1': [[0]],
    'TOWN_3_LEFT': [[0], [3], [4]],
    'TOWN_3_RIGHT': [[0], [2], [3]],
    'TOWN_3_HALF': [[0], [1], [2]],
    'TOWN_3_STAR': [[0], [2], [4]],
  };
  return connectionMap[tileType] || [];
}

/**
 * ペアがマッチするか判定（順序不問）
 * -1 は「中央」を表し、conn のどちらかの端にマッチ
 */
export function pairMatches(pair: [number, number], conn: number[]): boolean {
  // Townタイル: conn.length === 1 は edge → center の接続
  // pair = [-1, edge] または [edge, -1] にマッチ
  if (conn.length === 1) {
    const townEdge = conn[0];
    // pair が [-1, townEdge] または [townEdge, -1] ならマッチ
    if ((pair[0] === -1 && pair[1] === townEdge) ||
        (pair[0] === townEdge && pair[1] === -1)) {
      return true;
    }
    return false;
  }

  if (conn.length !== 2) return false;

  // 通常のマッチ
  if ((pair[0] === conn[0] && pair[1] === conn[1]) ||
      (pair[0] === conn[1] && pair[1] === conn[0])) {
    return true;
  }

  // -1（中央）を含むマッチ: pair = [-1, edge] または [edge, -1]
  // conn = [edge, other] の場合、edge 側の半分をマッチ
  if (pair[0] === -1 || pair[1] === -1) {
    const targetEdge = pair[0] === -1 ? pair[1] : pair[0];
    // conn に targetEdge が含まれていればマッチ
    return conn.includes(targetEdge);
  }

  return false;
}

/**
 * 地形に応じたfill色を返す
 */
export function getTerrainFill(terrain: string): string {
  switch (terrain) {
    case 'plains':
      return 'hsl(var(--terrain-grass))';
    case 'mountains':
      return 'hsl(var(--terrain-mountain))';
    case 'river':
    case 'water':
      return 'hsl(var(--terrain-water))';
    default:
      return 'hsl(var(--muted))';
  }
}

/**
 * 商品色に応じたfill色を返す
 */
export function getGoodsColorFill(color: string): string {
  switch (color) {
    case 'red':
      return 'hsl(var(--goods-red))';
    case 'blue':
      return 'hsl(var(--goods-blue))';
    case 'yellow':
      return 'hsl(var(--goods-yellow))';
    case 'purple':
      return 'hsl(var(--goods-purple))';
    case 'black':
      return 'hsl(var(--goods-black))';
    default:
      return 'hsl(var(--muted))';
  }
}

/**
 * 都市色に応じたfill色を返す
 * 配列の場合は最初の色を使用（TODO: 複数色の場合はグラデーション対応）
 */
export function getCityColorFill(color: string | string[]): string {
  const primaryColor = Array.isArray(color) ? color[0] : color;
  switch (primaryColor) {
    case 'red':
      return 'hsl(var(--goods-red))';
    case 'blue':
      return 'hsl(var(--goods-blue))';
    case 'yellow':
      return 'hsl(var(--goods-yellow))';
    case 'purple':
      return 'hsl(var(--goods-purple))';
    case 'black':
      return 'hsl(var(--goods-black))';
    default:
      return 'hsl(var(--muted))';
  }
}

/**
 * テキストを行に分割（日本語・英語両対応）
 * 日本語は任意の場所で折り返し、英語はスペースで折り返し
 */
export function splitTextIntoLines(text: string, maxWidth: number, charWidth: number, jpCharWidth: number): string[] {
  const lines: string[] = [];
  let currentLine = '';
  let currentWidth = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    // 日本語文字かどうかを判定（ひらがな、カタカナ、漢字、全角記号）
    const isJapanese = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\uff00-\uff9f]/.test(char);
    const cw = isJapanese ? jpCharWidth : charWidth;

    if (currentWidth + cw > maxWidth && currentLine.length > 0) {
      // 英語の場合はスペースで折り返しを試みる
      if (!isJapanese && char !== ' ') {
        const lastSpace = currentLine.lastIndexOf(' ');
        if (lastSpace > 0) {
          lines.push(currentLine.substring(0, lastSpace));
          currentLine = currentLine.substring(lastSpace + 1) + char;
          currentWidth = 0;
          for (const c of currentLine) {
            const isJp = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\uff00-\uff9f]/.test(c);
            currentWidth += isJp ? jpCharWidth : charWidth;
          }
          continue;
        }
      }
      lines.push(currentLine);
      currentLine = char;
      currentWidth = cw;
    } else {
      currentLine += char;
      currentWidth += cw;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
}

/**
 * テキストの表示幅を計算
 */
export function calculateTextWidth(text: string, charWidth: number, jpCharWidth: number): number {
  let width = 0;
  for (const char of text) {
    const isJapanese = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\uff00-\uff9f]/.test(char);
    width += isJapanese ? jpCharWidth : charWidth;
  }
  return width;
}
